import { Stack, Switch, Typography } from "@mui/material";
import { updateNotificationSetting } from "apis";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { fetchSetting, SettingState } from "store/features/setting";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

const settings = [
  { name: "meal", label: "Meal" },
  { name: "nappy", label: "Nappy" },
  { name: "sleep", label: "Sleep" },
  { name: "observation", label: "Observation" },
  { name: "assessment", label: "Assessment" },
  { name: "transitions", label: "Transitions" },
  { name: "photos", label: "Photos" },
  { name: "learning_story", label: "Learning story" },
  { name: "snapshot", label: "Snapshot" },
];

type SettingItemProp = {
  name: string;
  label: string;
  value: {
    email?: boolean;
    app?: boolean;
    push?: boolean;
  };
  onChange: (key: string, field: string, value: boolean) => void;
};

type INotificationSettings = {
  [prop: string]: {
    email?: boolean;
    app?: boolean;
    push?: boolean;
  };
};

const SettingItem: React.FC<SettingItemProp> = ({ name, value, onChange }) => {
  const { t } = useTranslation();

  return (
    <Stack direction="column" borderBottom={1} borderColor="lightgrey" p={1}>
      <Typography fontWeight="bold" variant="subtitle1" my={1}>
        {t(`posts.${name}`)}
      </Typography>

      <Stack direction="row" justifyContent="space-between">
        <Typography>{t("notifications.email")}</Typography>
        <Switch
          checked={!!value?.email}
          onChange={(e, checked) => onChange(name, "email", checked)}
        ></Switch>
      </Stack>

      <Stack direction="row" justifyContent="space-between">
        <Typography>{t("notifications.app")}</Typography>
        <Switch
          checked={!!value?.app}
          onChange={(e, checked) => onChange(name, "app", checked)}
        ></Switch>
      </Stack>

      <Stack direction="row" justifyContent="space-between">
        <Typography>{t("notifications.push")}</Typography>
        <Switch
          checked={!!value?.push}
          onChange={(e, checked) => onChange(name, "push", checked)}
        ></Switch>
      </Stack>
    </Stack>
  );
};

const NotificationSettings = forwardRef((_, ref) => {
  const [notificationSettings, setNotificationSettings] =
    useState<INotificationSettings>({});
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const { loaded, notification } = useSelector<RootState, SettingState>(
    (store) => store.setting,
  );
  const dispatch = useDispatch();

  useEffect(() => {
    if (loaded && notification) {
      setNotificationSettings(notification);
    }
  }, [loaded, notification]);

  useImperativeHandle(ref, () => {
    return {
      saveSettings: () => {
        updateNotificationSetting(notificationSettings)
          .then((data) => {
            dispatch(fetchSetting(data));
            enqueueSnackbar(t("setting.notification_updated"), {
              variant: "success",
            });
          })
          .catch(() => {
            enqueueSnackbar("Failed", { variant: "error" });
          });
      },
    };
  });

  return (
    <Stack direction="column">
      {settings.map(({ name, label }) => (
        <SettingItem
          key={name}
          name={name}
          label={label}
          value={notificationSettings[name]}
          onChange={(name, field, value) =>
            setNotificationSettings((settings) => ({
              ...settings,
              [name]: { ...settings[name], [field]: value },
            }))
          }
        />
      ))}
    </Stack>
  );
});

export default NotificationSettings;
